import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Heading } from "components/designV2/heading/heading";
import { PageHeaderRow } from "components/designV2/layout/pageLayout";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { UnitTeamDropdown } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  ControlsGrid,
  DateFilterSlot,
  DropdownsCell,
  OptionsCell,
  Toolbar,
  ViewTab,
  ViewTabs,
} from "./timeReportsToolbar.styled";

export type TimeReportsGroupByOption = "person" | "team";
export type TimeReportsView = "person" | "staffRegister";

interface TimeReportsToolbarProps {
  activeView: TimeReportsView;
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  personOptions: Array<{ value: string; label: string }>;
  unitTeamEmptyText: string;
  personEmptyText: string;
  selectedPersonIds: string[];
  monthDate: LocalDate;
  selectedRangeStart: LocalDate | null;
  isAllRowsExpanded: boolean;
  onActiveViewChange: (view: TimeReportsView) => void;
  onSelectedWorkGroupIdsChange: (values: string[]) => void;
  onSelectedPersonIdsChange: (values: string[]) => void;
  onDateRangeChange: (dates: LocalDate[]) => void;
  onExpandAllClick: () => void;
  onExportClick: () => void;
  onOptionsClick: () => void;
}

export const TimeReportsToolbar: React.FC<TimeReportsToolbarProps> = (
  props,
) => {
  const { t } = useTranslation();
  const isPersonView = props.activeView === "person";

  return (
    <Toolbar>
      <PageHeaderRow>
        <Heading level="h2">{t("TimeReports.TimeReports")}</Heading>
      </PageHeaderRow>
      <ControlsGrid $hasDropdowns={isPersonView}>
        <ViewTabs>
          <ViewTab
            type="button"
            $isActive={isPersonView}
            onClick={() => props.onActiveViewChange("person")}
          >
            {t("TimeReports.Person")}
          </ViewTab>
          <ViewTab
            type="button"
            $isActive={!isPersonView}
            onClick={() => props.onActiveViewChange("staffRegister")}
          >
            {t("TimeReports.StaffRegister")}
          </ViewTab>
        </ViewTabs>
        {isPersonView && (
          <DropdownsCell>
            <UnitTeamDropdown
              label={t("TimeReports.UnitTeam")}
              placeholder={t("TimeReports.AllUnitsTeamsVisible")}
              emptyText={props.unitTeamEmptyText}
              options={props.unitTeamOptions}
              selectedTeamIds={props.selectedWorkGroupIds}
              onSelectedTeamIdsChange={props.onSelectedWorkGroupIdsChange}
            />
            <SearchableDropdown
              label={t("TimeReports.Person")}
              placeholder={t("TimeReports.SearchOrBrowse")}
              emptyText={props.personEmptyText}
              options={props.personOptions}
              leadingIconName="v2/search"
              multiSelect
              selectedValues={props.selectedPersonIds}
              onSelectedValuesChange={props.onSelectedPersonIdsChange}
              onClear={() => props.onSelectedPersonIdsChange([])}
            />
            <DateFilterSlot>
              <DatePicker
                key={`time-reports-date-${props.monthDate.toString()}`}
                label={t("CompanyTimeReport.Date")}
                allowRangePicker
                allowMultiSelectInSingleMode={false}
                allowClear
                value={props.selectedRangeStart}
                onRangeChange={props.onDateRangeChange}
                restrictToMonth={props.monthDate.withDayOfMonth(1)}
                disableNavigationOutsideMonth
              />
            </DateFilterSlot>
          </DropdownsCell>
        )}
        <OptionsCell>
          {isPersonView ? (
            <>
              <SecondaryButton
                label={
                  props.isAllRowsExpanded
                    ? t("TimeReports.CollapseAll")
                    : t("TimeReports.ExpandAll")
                }
                size="small"
                iconName="v2/expand-vertical"
                onClick={props.onExpandAllClick}
              />
              <SecondaryButton
                label={t("TimeReports.Options")}
                size="small"
                iconName="v2/settings"
                onClick={props.onOptionsClick}
              />
            </>
          ) : (
            <SecondaryButton
              label={t("TimeReports.Export")}
              size="small"
              iconName="v2/download"
              onClick={props.onExportClick}
            />
          )}
        </OptionsCell>
      </ControlsGrid>
    </Toolbar>
  );
};
